// PUBLIC contractor directory — /contractors and /contractors/:service
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Search, MapPin, Filter, Loader2, CalendarDays, Users, Trophy, HardHat, List, Map as MapIcon,
  ShieldCheck, FileText, BadgeCheck, Camera, ArrowRight, X,
} from "lucide-react";
import { Header, EnquiryForm } from "@/pages/PublicProfile";
import { MarketingFooter } from "@/components/marketing/MarketingSections2";

const API = process.env.REACT_APP_BACKEND_URL;
const img = (p) => `${API}/api/public/bp-image?path=${encodeURIComponent(p)}`;
const POPULAR = ["Earthworks", "Drainage", "Retaining", "Concrete", "Subdivisions", "Roading", "Utilities", "Siteworks"];
const REGIONS = ["Auckland", "Northland", "Waikato", "Bay of Plenty", "Gisborne", "Hawke's Bay", "Taranaki", "Manawatū-Whanganui", "Wellington", "Nelson / Tasman", "Marlborough", "West Coast", "Canterbury", "Otago", "Southland"];
const SIDEBAR_SERVICES = ["Earthworks", "Drainage", "Retaining", "Concrete", "Roading", "Subdivisions", "Utilities", "Siteworks", "Cartage"];
const CONTRACTOR_TYPES = ["Main Contractor", "Specialist Subcontractor", "Plant Hire", "Material Supplier"];
const ACCREDITATIONS = ["SiteWise", "Totika", "CHAS NZ", "SHE Prequal", "Other"];
const HERO = "https://images.unsplash.com/photo-1523848309072-c199db53f137?w=1800&q=65";
const FALLBACKS = [
  "https://images.unsplash.com/photo-1517089596392-fb9a9033e05b?w=800&q=60",
  "https://images.unsplash.com/photo-1621922688758-359fc864071e?w=800&q=60",
  "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=60",
  "https://images.unsplash.com/photo-1531834685032-c34bf0d84c77?w=800&q=60",
  "https://images.unsplash.com/photo-1508450859948-4e04fabaa4ea?w=800&q=60",
];
const CTA_FIND = "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1000&q=60";
const CTA_LIST = "https://images.unsplash.com/photo-1621922688758-359fc864071e?w=1000&q=60";

const Check = ({ checked, onChange, label, testId }) => (
  <label className="flex items-center gap-2.5 text-[13px] font-semibold text-[#465562] cursor-pointer group">
    <input type="checkbox" checked={checked} onChange={onChange} className="w-4 h-4 accent-[#FF5F15] rounded" data-testid={testId} />
    <span className="group-hover:text-[#142635]">{label}</span>
  </label>
);

const FilterHead = ({ children }) => (
  <div className="text-[10.5px] font-black uppercase tracking-[0.14em] text-[#465562] mb-2.5">{children}</div>
);

export default function Directory() {
  const { service: routeService } = useParams();
  const [params, setParams] = useSearchParams();
  const [svc, setSvc] = useState(routeService || params.get("service") || "");
  const [loc, setLoc] = useState(params.get("location") || "");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sort, setSort] = useState("relevance");
  const [enquire, setEnquire] = useState(null); // {slug, services}
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [taxonomy, setTaxonomy] = useState(null);
  const [suggest, setSuggest] = useState([]);
  // sidebar filter state
  const [fServices, setFServices] = useState([]);
  const [fTypes, setFTypes] = useState([]);
  const [fCaps, setFCaps] = useState([]);
  const [fAccs, setFAccs] = useState([]);
  const [fRegion, setFRegion] = useState("");
  const [fRadius, setFRadius] = useState("50");

  useEffect(() => {
    axios.get(`${API}/api/public/directory/taxonomy`).then((r) => setTaxonomy(r.data.taxonomy)).catch(() => {});
  }, []);
  const onSvcType = (v) => {
    setSvc(v);
    const q = v.trim().toLowerCase();
    if (!q || !taxonomy) { setSuggest([]); return; }
    const out = [];
    for (const [cat, subs] of Object.entries(taxonomy)) {
      if (cat.toLowerCase().includes(q)) out.push(cat);
      for (const s of subs || []) if (s.toLowerCase().includes(q)) out.push(s);
    }
    setSuggest([...new Set(out)].slice(0, 7));
  };
  const pickSuggest = (s) => { setSvc(s); setSuggest([]); setParams({ service: s, ...(loc && { location: loc }) }); run(s, loc); };

  const run = async (s = svc, l = loc, so = sort) => {
    setBusy(true);
    try {
      const { data: d } = await axios.get(`${API}/api/public/directory/search`, { params: { service: s || undefined, location: l || undefined, sort: so } });
      setData(d);
    } catch { setData({ count: 0, results: [] }); } finally { setBusy(false); }
  };
  useEffect(() => {
    document.title = svc ? `${svc} Contractors${loc ? ` in ${loc}` : ""} | Fieldlink Directory` : "Find Civil Contractors NZ | Fieldlink";
    run();
    // eslint-disable-next-line
  }, []);
  const submit = () => { setParams({ ...(svc && { service: svc }), ...(loc && { location: loc }) }); run(); };
  const chip = (s) => { setSvc(s); setParams({ service: s, ...(loc && { location: loc }) }); run(s, loc); };

  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const applyFilters = () => {
    const l = fRegion || loc;
    setLoc(l);
    setFiltersOpen(false);
    setParams({ ...(svc && { service: svc }), ...(l && { location: l }) });
    run(svc, l);
  };
  const clearFilters = () => {
    setFServices([]); setFTypes([]); setFCaps([]); setFAccs([]); setFRegion(""); setFRadius("50");
    setSvc(""); setLoc(""); setParams({});
    run("", "");
  };

  // client-side refinement on top of backend results
  const shown = useMemo(() => {
    let list = data?.results || [];
    if (fServices.length) {
      const sel = fServices.map((s) => s.toLowerCase());
      list = list.filter((r) => {
        const have = [...(r.services || []), ...(r.specialist_services || [])].map((x) => x.toLowerCase());
        return sel.some((s) => have.some((h) => h.includes(s) || s.includes(h)));
      });
    }
    if (fCaps.includes("Has public projects")) list = list.filter((r) => (r.project_count || 0) > 0);
    if (fAccs.length) {
      const sel = fAccs.map((a) => a.toLowerCase());
      list = list.filter((r) => (r.accreditations || []).some((a) => sel.some((s) => (a || "").toLowerCase().includes(s))));
    }
    return list;
  }, [data, fServices, fCaps, fAccs]);

  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <div className="bg-[#f6f5f1] min-h-screen text-[#142635]" data-testid="directory-page">
      <Header />

      {/* ============ HERO + SEARCH ============ */}
      <div className="relative bg-[#f6f5f1]">
        <img src={HERO} alt="Civil construction site in New Zealand" className="absolute inset-0 w-full h-full object-cover opacity-45" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/55 via-white/45 to-white/75" />
        <div className="relative max-w-[1280px] mx-auto px-4 md:px-8 pt-16 md:pt-20 pb-12 md:pb-14">
          <div className="text-[#a63c0b] text-[11px] font-mono font-bold uppercase tracking-[0.28em]" data-testid="dir-hero-kicker">Real businesses. Real capability.</div>
          <h1 className="font-display font-black uppercase text-[#142635] text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tight mt-2.5">
            Find Civil Contractors<br />
            <span className="text-[#a63c0b]">in New Zealand</span>
          </h1>
          <p className="text-[#465562] mt-3 text-base md:text-lg">Trusted businesses. Real capability. Built for civil.</p>

          {/* search panel */}
          <div className="mt-7 bg-white backdrop-blur border border-[#cbd3d5] rounded-xl shadow-[0_30px_80px_rgba(0,0,0,0.6)] p-3 flex flex-col md:flex-row gap-2 max-w-[900px]" data-testid="dir-search-panel">
            <div className="relative flex items-center gap-2 flex-1 border border-[#cbd3d5] bg-white rounded-lg px-3 h-[52px]">
              <Search className="w-4 h-4 text-[#52616c] shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-[9px] font-bold uppercase tracking-widest text-[#52616c]">What do you need?</div>
                <input value={svc} onChange={(e) => onSvcType(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (setSuggest([]), submit())} onBlur={() => setTimeout(() => setSuggest([]), 150)}
                  placeholder="Earthworks, Drainage, Retaining..." className="w-full bg-transparent text-[#142635] text-sm font-semibold outline-none placeholder:text-[#52616c]" data-testid="dir-service-input" />
              </div>
              {suggest.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#cbd3d5] rounded-lg shadow-[0_30px_80px_rgba(0,0,0,0.7)] z-20 overflow-hidden" data-testid="dir-autocomplete">
                  {suggest.map((s) => (
                    <button key={s} onMouseDown={() => pickSuggest(s)} className="block w-full text-left px-3.5 py-2.5 text-sm font-semibold text-[#344451] hover:bg-[#FF5F15]/10 hover:text-[#a63c0b]" data-testid={`dir-suggest-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-1 border border-[#cbd3d5] bg-white rounded-lg px-3 h-[52px]">
              <MapPin className="w-4 h-4 text-[#52616c] shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-[9px] font-bold uppercase tracking-widest text-[#52616c]">Where?</div>
                <input value={loc} onChange={(e) => setLoc(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()}
                  placeholder="Auckland, Hamilton, Tauranga..." className="w-full bg-transparent text-[#142635] text-sm font-semibold outline-none placeholder:text-[#52616c]" data-testid="dir-location-input" />
              </div>
            </div>
            <button onClick={submit} className="h-[52px] px-7 bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] text-[12px] font-black uppercase tracking-wider rounded-lg transition-colors" data-testid="dir-search-btn">
              Search contractors
            </button>
          </div>

          {/* popular chips */}
          <div className="flex items-center gap-1.5 mt-4 flex-wrap">
            <span className="text-[11px] font-bold text-[#52616c] uppercase tracking-wider mr-1">Popular searches:</span>
            {POPULAR.map((s) => (
              <button key={s} onClick={() => chip(s)} data-testid={`dir-chip-${s.toLowerCase()}`}
                className={`h-7 px-3.5 rounded-full text-[11px] font-bold border transition-colors ${svc === s ? "bg-[#FF5F15] border-[#FF5F15] text-[#142635]" : "border-[#cbd3d5] text-[#465562] hover:border-[#FF5F15] hover:text-[#142635]"}`}>{s}</button>
            ))}
          </div>
        </div>
      </div>

      {/* ============ TRUST STRIP ============ */}
      <div className="bg-[#e9eeec] border-y border-[#cbd3d5]" data-testid="dir-trust-strip">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-5 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[[ShieldCheck, "Verified businesses", "Real civil contractors"],
            [MapPin, "Across New Zealand", "Find local teams"],
            [Users, "Built for civil", "Specialist capabilities"],
            [FileText, "Request a quote", "Get in touch directly"]].map(([Icon, t, d]) => (
            <div key={t} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#FF5F15]/10 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 text-[#a63c0b]" strokeWidth={2} />
              </div>
              <div>
                <div className="text-[11.5px] font-black uppercase tracking-wider text-[#142635]">{t}</div>
                <div className="text-[11px] text-[#52616c]">{d}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ============ MAIN: FILTERS + RESULTS ============ */}
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-7">
        {/* results header */}
        <div className="flex items-center gap-3 flex-wrap">
          <div>
            <h2 className="font-display font-black uppercase text-xl md:text-2xl tracking-tight" data-testid="dir-results-heading">
              {svc || "Civil"} contractors{loc ? ` in ${loc}` : " in New Zealand"}
            </h2>
            <span className="text-[13px] text-[#52616c]" data-testid="dir-results-count">{shown.length} business{shown.length !== 1 ? "es" : ""} found</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="hidden md:inline-flex rounded-lg border border-[#cbd3d5] overflow-hidden bg-white">
              <span className="px-3 h-9 inline-flex items-center gap-1.5 text-[11px] font-bold bg-[#FF5F15] text-[#142635]"><List className="w-3.5 h-3.5" /> List</span>
              <span className="px-3 h-9 inline-flex items-center gap-1.5 text-[11px] font-bold text-[#52616c] cursor-not-allowed" title="Map view coming soon"><MapIcon className="w-3.5 h-3.5" /> Map</span>
            </span>
            <span className="hidden md:inline text-[11px] font-bold text-[#52616c] uppercase tracking-wider">Sort by</span>
            <select value={sort} onChange={(e) => { setSort(e.target.value); run(svc, loc, e.target.value); }}
              className="h-9 border border-[#cbd3d5] rounded-lg px-2.5 text-[12px] font-semibold bg-white text-[#142635]" data-testid="dir-sort">
              <option value="relevance">Relevance</option>
              <option value="projects">Most projects</option>
              <option value="updated">Recently updated</option>
            </select>
            <button onClick={() => setFiltersOpen(true)} className="lg:hidden h-9 px-3.5 border border-[#cbd3d5] bg-white text-[#142635] rounded-lg text-[11px] font-bold inline-flex items-center gap-1.5" data-testid="dir-mobile-filters-btn"><Filter className="w-3.5 h-3.5" /> Filters</button>
          </div>
        </div>

        <div className="grid lg:grid-cols-[280px_1fr] gap-6 mt-5 items-start">
          {/* ===== FILTER SIDEBAR ===== */}
          <aside className={`${filtersOpen ? "fixed inset-0 z-50 bg-black/50 lg:static lg:bg-transparent lg:z-auto" : "hidden lg:block"}`} onClick={(e) => { if (e.target === e.currentTarget) setFiltersOpen(false); }}>
            <div className={`bg-white rounded-xl border border-[#cbd3d5] p-5 ${filtersOpen ? "absolute right-0 top-0 bottom-0 w-[320px] max-w-[88vw] overflow-y-auto rounded-none lg:static lg:w-auto lg:rounded-xl lg:overflow-visible" : ""}`} data-testid="dir-filter-sidebar">
              <div className="flex items-center justify-between mb-4">
                <div className="font-display font-black uppercase text-base">Filters</div>
                <div className="flex items-center gap-3">
                  <button onClick={clearFilters} className="text-[11.5px] font-bold text-[#a63c0b] hover:underline" data-testid="dir-clear-all">Clear all</button>
                  <button onClick={() => setFiltersOpen(false)} className="lg:hidden text-[#52616c]" data-testid="dir-close-filters"><X className="w-4 h-4" /></button>
                </div>
              </div>

              <FilterHead>Location</FilterHead>
              <select value={fRegion} onChange={(e) => setFRegion(e.target.value)} className="w-full h-10 border border-[#cbd3d5] rounded-lg px-2.5 text-[13px] font-semibold bg-white text-[#142635] outline-none focus:border-[#FF5F15]" data-testid="dir-region-select">
                <option value="">All of New Zealand</option>
                {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <select value={fRadius} onChange={(e) => setFRadius(e.target.value)} className="w-full h-10 border border-[#cbd3d5] rounded-lg px-2.5 text-[13px] font-semibold bg-white text-[#142635] outline-none focus:border-[#FF5F15] mt-2" data-testid="dir-radius-select">
                {["25", "50", "100"].map((k) => <option key={k} value={k}>Within {k} km</option>)}
                <option value="region">Whole region</option>
              </select>

              <div className="border-t border-[#cbd3d5] my-4" />
              <FilterHead>Services</FilterHead>
              <div className="space-y-2">
                {SIDEBAR_SERVICES.map((s) => (
                  <Check key={s} checked={fServices.includes(s)} onChange={() => toggle(fServices, setFServices, s)} label={s} testId={`dir-filter-${s.toLowerCase()}`} />
                ))}
              </div>

              <div className="border-t border-[#cbd3d5] my-4" />
              <FilterHead>Contractor type</FilterHead>
              <div className="space-y-2">
                {CONTRACTOR_TYPES.map((t) => (
                  <Check key={t} checked={fTypes.includes(t)} onChange={() => toggle(fTypes, setFTypes, t)} label={t} testId={`dir-type-${t.toLowerCase().replace(/\s+/g, "-")}`} />
                ))}
              </div>

              <div className="border-t border-[#cbd3d5] my-4" />
              <FilterHead>Capabilities</FilterHead>
              <div className="space-y-2">
                {["Has public projects", "Plant capability", "Accepting enquiries"].map((c) => (
                  <Check key={c} checked={fCaps.includes(c)} onChange={() => toggle(fCaps, setFCaps, c)} label={c} testId={`dir-cap-${c.toLowerCase().replace(/\s+/g, "-")}`} />
                ))}
              </div>

              <div className="border-t border-[#cbd3d5] my-4" />
              <FilterHead>Accreditations</FilterHead>
              <div className="space-y-2">
                {ACCREDITATIONS.map((a) => (
                  <Check key={a} checked={fAccs.includes(a)} onChange={() => toggle(fAccs, setFAccs, a)} label={a} testId={`dir-acc-${a.toLowerCase().replace(/\s+/g, "-")}`} />
                ))}
              </div>

              <button onClick={applyFilters} className="mt-5 w-full h-11 bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] text-[12px] font-black uppercase tracking-wider rounded-lg transition-colors" data-testid="dir-apply-filters">Apply filters</button>
              <button onClick={clearFilters} className="mt-2 w-full h-11 border border-[#cbd3d5] hover:border-[#cbd3d5] text-[#465562] text-[12px] font-black uppercase tracking-wider rounded-lg transition-colors bg-white" data-testid="dir-clear-filters">Clear</button>
            </div>
          </aside>

          {/* ===== RESULT CARDS ===== */}
          <div className="space-y-5 min-w-0">
            {busy && <div className="flex justify-center py-14"><Loader2 className="w-7 h-7 animate-spin text-[#a63c0b]" /></div>}
            {!busy && shown.length === 0 && (
              <div className="bg-white rounded-xl border border-[#cbd3d5] p-10 text-center" data-testid="dir-no-results">
                <HardHat className="w-8 h-8 mx-auto text-[#52616c]" />
                <p className="font-display font-black uppercase mt-2">No contractors found</p>
                <p className="text-sm text-[#52616c] mt-1">Try a broader service or location — or invite your contractor to join Fieldlink.</p>
              </div>
            )}
            {!busy && shown.map((r, i) => (
              <div key={r.slug} className="bg-white rounded-xl border border-[#cbd3d5] overflow-hidden flex flex-col md:flex-row hover:border-[#FF5F15]/40 transition-colors" data-testid={`dir-card-${r.slug}`}>
                {/* photo */}
                <Link to={`/company/${r.slug}`} className="relative md:w-[300px] shrink-0 block group">
                  <img src={r.cover_path ? img(r.cover_path) : FALLBACKS[i % FALLBACKS.length]} alt={`${r.name} project work`} loading="lazy"
                    className="h-48 md:h-full md:min-h-[230px] w-full object-cover group-hover:scale-[1.02] transition-transform duration-300" />
                  {(r.project_count || 0) > 0 && (
                    <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-[#e9eeec]/85 backdrop-blur text-[#142635] text-[10px] font-black uppercase tracking-wider rounded-md px-2.5 py-1.5">
                      <Trophy className="w-3 h-3 text-[#a63c0b]" /> {r.project_count}+ project{r.project_count !== 1 ? "s" : ""}
                    </span>
                  )}
                  <span className="absolute bottom-3 right-3 w-8 h-8 rounded-md bg-white/90 backdrop-blur flex items-center justify-center">
                    <Camera className="w-4 h-4 text-[#142635]" />
                  </span>
                </Link>
                {/* details */}
                <div className="p-5 flex-1 min-w-0 flex flex-col">
                  <div className="flex items-start gap-3">
                    {r.logo_path ? (
                      <img src={img(r.logo_path)} alt={`${r.name} logo`} className="w-12 h-12 object-contain rounded-lg border border-[#cbd3d5] bg-white p-1 shrink-0" loading="lazy" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-[#FF5F15] text-[#142635] font-display font-black text-lg flex items-center justify-center shrink-0">{(r.name || "?").charAt(0)}</div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link to={`/company/${r.slug}`} className="font-display font-black uppercase text-[17px] leading-tight hover:text-[#a63c0b] transition-colors">{r.name}</Link>
                        <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-0.5" data-testid={`dir-verified-${r.slug}`}>
                          <BadgeCheck className="w-3 h-3" /> Verified
                        </span>
                      </div>
                      <div className="text-[12px] text-[#52616c] flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3 h-3 text-[#a63c0b]" /> {(r.service_areas || []).slice(0, 3).join(" • ") || r.location || "New Zealand"}
                      </div>
                    </div>
                  </div>
                  {(r.about || r.tagline) && <p className="text-[13px] text-[#52616c] mt-2.5 line-clamp-2">{r.about || r.tagline}</p>}
                  <div className="flex flex-wrap gap-1.5 mt-2.5">
                    {(r.services || []).slice(0, 5).map((s) => (
                      <span key={s} className="text-[9.5px] font-black uppercase tracking-wider bg-[#FF5F15]/10 text-[#a63c0b] rounded px-2 py-1">{s}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-4 mt-auto pt-3.5 flex-wrap">
                    {r.stats?.years && <span className="text-[11.5px] font-bold text-[#52616c] inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-[#a63c0b]" /> {r.stats.years}+ years</span>}
                    {r.stats?.projects_completed && <span className="text-[11.5px] font-bold text-[#52616c] inline-flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5 text-[#a63c0b]" /> {r.stats.projects_completed}+ projects</span>}
                    {r.stats?.team_size && <span className="text-[11.5px] font-bold text-[#52616c] inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-[#a63c0b]" /> {r.stats.team_size} staff</span>}
                    <span className="ml-auto flex gap-2">
                      <button onClick={() => setEnquire({ slug: r.slug, services: r.services || [] })}
                        className="h-10 px-4 bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] rounded-lg text-[11px] font-black uppercase tracking-wider transition-colors" data-testid={`dir-quote-${r.slug}`}>Request a quote</button>
                      <Link to={`/company/${r.slug}`} className="h-10 px-4 inline-flex items-center border border-[#cbd3d5] bg-white text-[#142635] hover:border-[#FF5F15] hover:text-[#a63c0b] rounded-lg text-[11px] font-black uppercase tracking-wider transition-colors" data-testid={`dir-view-${r.slug}`}>View profile</Link>
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ============ BOTTOM CTA CARDS ============ */}
        <div className="grid md:grid-cols-2 gap-5 mt-12" data-testid="dir-bottom-ctas">
          <div className="relative rounded-2xl overflow-hidden min-h-[280px] flex">
            <img src={CTA_FIND} alt="Civil construction crew on site" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-white/85 via-white/55 to-white/35" />
            <div className="relative p-7 md:p-9 mt-auto">
              <div className="text-[#a63c0b] text-[10.5px] font-black uppercase tracking-[0.2em]">Have a project coming up?</div>
              <h3 className="font-display font-black uppercase text-[#142635] text-xl md:text-2xl leading-tight mt-1.5">Find the right civil contractor for your next job.</h3>
              <p className="text-[#465562] text-[13px] mt-1.5">Tell us what you need and we'll help you connect with suitable Fieldlink businesses.</p>
              <button onClick={scrollTop} className="mt-4 inline-flex h-11 items-center gap-2 px-6 bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] text-[11.5px] font-black uppercase tracking-wider rounded-lg transition-colors" data-testid="dir-cta-find">
                Find a contractor <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="relative rounded-2xl overflow-hidden min-h-[280px] flex">
            <img src={CTA_LIST} alt="Heavy machinery on a civil site" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-white/85 via-white/55 to-white/35" />
            <div className="relative p-7 md:p-9 mt-auto">
              <div className="text-[#a63c0b] text-[10.5px] font-black uppercase tracking-[0.2em]">Are you a civil contractor?</div>
              <h3 className="font-display font-black uppercase text-[#142635] text-xl md:text-2xl leading-tight mt-1.5">List your business on Fieldlink.</h3>
              <p className="text-[#465562] text-[13px] mt-1.5">Show your work. Get found. Win more work.</p>
              <a href="/register" className="mt-4 inline-flex h-11 items-center gap-2 px-6 bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] text-[11.5px] font-black uppercase tracking-wider rounded-lg transition-colors" data-testid="dir-cta-list">
                List your business <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <MarketingFooter />
      {enquire && <EnquiryForm slug={enquire.slug} services={enquire.services} source="directory" searchService={svc} searchLocation={loc} onClose={() => setEnquire(null)} />}
    </div>
  );
}
